import * as yup from 'yup';

/**
 * E35 · Assessments 2 — the both-ends input codecs of the three new mutations (plan 03 §2.1 #3): the portal and — as the
 * shield's inputRule — the API run the SAME schemas. SHAPE only; the business rules (the score, the answer count and range,
 * the future date, the relation, the definition's structure) are the deciders', where they are binding. `performer` and
 * `practiceId` are ABSENT on purpose: the caller is the performer and their practice rides from the session.
 */
const userRef = yup
  .string()
  .trim()
  .matches(/^users\/[A-Za-z0-9_-]{1,64}$/);

const uuid = yup
  .string()
  .trim()
  .matches(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);

export const publishInstrumentInputSchema = yup.object({
  instrumentId: yup.string().trim().matches(/^[a-z][a-z0-9]{1,30}$/).required(),
  name: yup.string().trim().min(1).max(120).required(),
  nameRu: yup.string().trim().max(200).nullable(),
  itemsCount: yup.number().integer().min(1).max(200).required(),
  responseScale: yup
    .object({
      min: yup.number().integer().required(),
      max: yup.number().integer().required(),
      labels: yup.array().of(yup.string().trim().min(1).max(160).required()).min(2).max(11).required(),
    })
    .required(),
  scoringRule: yup
    .string()
    .oneOf(['sum', 'subscaleSumTimes2', 'reverseKeyedYesNo', 'sumWithClusters', 'uricaReadiness'])
    .required(),
  subscales: yup
    .array()
    .of(
      yup
        .object({
          name: yup.string().trim().min(1).max(60).required(),
          items: yup.array().of(yup.number().integer().required()).min(1).max(200).required(),
          multiplier: yup.number().integer().min(1).max(10).nullable(),
          reverseItems: yup.array().of(yup.number().integer().required()).max(200).nullable(),
        })
        .required(),
    )
    .min(1)
    .max(20)
    .required(),
  bands: yup
    .array()
    .of(
      yup
        .object({
          subscale: yup.string().trim().max(60).nullable(),
          bands: yup
            .array()
            .of(
              yup
                .object({
                  min: yup.number().integer().required(),
                  max: yup.number().integer().required(),
                  band: yup.string().trim().matches(/^[A-Za-z][A-Za-z0-9]{0,40}$/).required(),
                })
                .required(),
            )
            .min(1)
            .max(12)
            .required(),
        })
        .required(),
    )
    .max(20)
    .required(),
  licenceClass: yup.string().oneOf(['publicDomain', 'attribution', 'licensed']).required(),
  availableFor: yup.string().oneOf(['public', 'assignedOnly']).required(),
  languages: yup.array().of(yup.string().oneOf(['en', 'ru']).required()).min(1).max(2).required(),
  itemsKey: yup.string().trim().min(1).max(80).required(),
  citation: yup.string().trim().min(1).max(600).required(),
});

export const recordExternalAssessmentInputSchema = yup.object({
  externalAssessmentId: uuid.required(),
  subject: userRef.required(),
  instrumentName: yup.string().trim().min(1).max(120).required(),
  score: yup.number().integer().min(0).max(100000).required(),
  maxScore: yup.number().integer().min(1).max(100000).nullable(),
  administeredAt: yup.mixed().required(),
  note: yup.string().max(2000).nullable(),
});

export const createAssessmentInSessionInputSchema = yup.object({
  assessmentId: uuid.required(),
  subject: userRef.required(),
  type: yup.number().integer().min(0).max(20).required(),
  answers: yup.array().of(yup.number().integer().nullable().defined()).min(1).max(200).required(),
  // the URICA's readiness is stored x 10 and runs -20…140 (C + A + M - PC over the subscale means); every other
  // instrument's score is >= 0. The decider recomputes the exact score from the answers (ASSESSMENT_SCORE_MISMATCH).
  score: yup.number().integer().min(-20).max(1000).required(),
});

export const publishInstrumentArgsSchema = yup.object({
  input: publishInstrumentInputSchema.required(),
});
export const recordExternalAssessmentArgsSchema = yup.object({
  input: recordExternalAssessmentInputSchema.required(),
});
export const createAssessmentInSessionArgsSchema = yup.object({
  input: createAssessmentInSessionInputSchema.required(),
});

// Typed loosely on purpose (TS7056 — see diaryCard.ts): the shape is the Yup schemas above.
export const assessmentValidators: Record<
  | 'publishInstrumentInputSchema'
  | 'recordExternalAssessmentInputSchema'
  | 'createAssessmentInSessionInputSchema'
  | 'publishInstrumentArgsSchema'
  | 'recordExternalAssessmentArgsSchema'
  | 'createAssessmentInSessionArgsSchema',
  yup.AnyObjectSchema
> = {
  publishInstrumentInputSchema,
  recordExternalAssessmentInputSchema,
  createAssessmentInSessionInputSchema,
  publishInstrumentArgsSchema,
  recordExternalAssessmentArgsSchema,
  createAssessmentInSessionArgsSchema,
};
